import React, { useState } from 'react';
import PropTypes from 'prop-types';
import SunburstChart from './SunburstChart';
import { METRIC_LABELS } from './Constants';

// The Read (v2, DER_READ_PAGE_SPEC): a ranked list of insight rows. Each opens to its craft
// explanation; a click-to-reveal 3-step table then shows how to see it on the wheel — (1) the
// Colour-by control preset to this insight's metric, (2) a mini-wheel + the data reading, (3) a
// hand-off to the live wheel. One strength row (green). The wheel GRAMMAR is not repeated here — it
// lives in the PageIntro banner above.

const NOOP = () => {};
const EMPTY = [];   // stable empty highlight so each mini-wheel stays isolated from the live wheel
const NAVY = '#14536e', GREEN = '#1f7a5a', PEACH = '#b06a14', RED = '#a23a2e', SLATE = '#33454f', MUTE = '#7b8a96';
const BAND_LABEL = { keep: 'In good shape', polish: 'Polish', rework: 'Needs a rework', rebuild: 'Big rebuild', cut_or_combine: 'Cut / combine' };
const BAND_COLOR = { keep: GREEN, polish: '#3b6fb0', rework: PEACH, rebuild: RED, cut_or_combine: RED };
const AXES = ['contract', 'pressure', 'development', 'movement', 'consequence', 'economy', 'assembly', 'clarity', 'freshness', 'unpredictability', 'charge', 'grip'];

// The metric that best SHOWS a card's pattern, from its canonical claim (schema-derived, deterministic):
// an axis if the claim names one (the driver), else the dragging (low) lens, else triage.
function metricForClaim(claim) {
  if (!claim) return 'overall';
  const ax = Array.isArray(claim.axis_chain) ? claim.axis_chain.filter(a => AXES.includes(a)) : [];
  if (ax.length) return ax[0];
  const ln = Array.isArray(claim.lens_chain) ? claim.lens_chain : [];
  if (ln.length) return ln[ln.length - 1];
  return 'overall';
}
function coverageText(fp) {
  const m = fp && fp.summary && /(\d+)\s+of\s+(\d+)/.exec(fp.summary);
  return m ? `touches ${m[1]}/${m[2]}` : null;
}
function coveragePct(fp) {
  const m = fp && fp.summary && /(\d+)\s+of\s+(\d+)/.exec(fp.summary);
  return m ? Math.round((+m[1] / +m[2]) * 100) : null;
}
function coolRange(g, card) {
  const src = `${(card && card.writer_action) || ''} ${(g && g.first_move) || ''} ${(card && card.footprint && card.footprint.summary) || ''}`;
  const m = /scenes?\s+(\d+)\s*[–-]\s*(\d+)/i.exec(src);
  return (m && +m[1] > 1) ? `${+m[1]}–${+m[2]}` : null;
}
// TEMPLATE wheel reading — the fallback until the engine's per-card wheel_caption lands (spec §5).
function templateWheelCaption(card, metricLabel, g, isStrength) {
  const pct = coveragePct(card.footprint);
  const range = coolRange(g, card);
  if (isStrength) {
    return `Coloured by ${metricLabel}, this reads green across the script — that's the strength you're protecting. Keep it while you sharpen the rest.`;
  }
  return `Coloured by ${metricLabel}, the low-scoring units light up${pct ? ` — about ${pct}% of the script` : ''}. Look for where the red runs deepest${range ? ` (around scenes ${range})` : ''} — that's where the work pays off first.`;
}

const stepNum = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '50%', background: NAVY, color: '#fff', fontSize: 12, fontWeight: 800, marginRight: 8, flexShrink: 0 };

// Step 1 — a static illustration of the real Colour-by control, this metric highlighted.
function FakeColourBy({ metricLabel }) {
  return (
    <div style={{ display: 'inline-block', border: `1.5px solid ${NAVY}`, borderRadius: 8, overflow: 'hidden', fontSize: '.85rem', minWidth: 150, background: '#fff' }}>
      <div style={{ padding: '.3rem .6rem', color: MUTE, borderBottom: '1px solid #e2e8f0', fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em' }}>Colour by ▾</div>
      {['Triage', metricLabel].map((opt, i) => (
        <div key={i} style={{ padding: '.3rem .6rem', background: opt === metricLabel ? '#e7f0f5' : '#fff', color: opt === metricLabel ? NAVY : '#64748b', fontWeight: opt === metricLabel ? 700 : 500 }}>
          {opt === metricLabel ? '‣ ' : '  '}{opt}
        </div>
      ))}
    </div>
  );
}
FakeColourBy.propTypes = { metricLabel: PropTypes.string };

// The 3-step reveal — lazy-mounted (only rendered when opened), so the mini-wheel isn't built until asked.
function WheelReveal({ card, metric, metricLabel, movie, g, isStrength, onExplore }) {
  return (
    <div style={{ border: '1px solid #dbe6ee', borderRadius: 10, background: '#f7fbfd', marginTop: '.7rem', overflow: 'hidden' }}>
      {/* 1 — select the control (same column widths as step 2, so the steps line up as instructions) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 340px) 1fr', gap: '1.3rem', alignItems: 'center', padding: '.85rem 1rem', borderBottom: '1px solid #e6eef3' }}>
        <div className="d-flex align-items-center"><span style={stepNum}>1</span><FakeColourBy metricLabel={metricLabel} /></div>
        <div style={{ color: SLATE, fontSize: '.9rem', lineHeight: 1.5 }}>
          Select <b style={{ color: NAVY }}>{metricLabel}</b> from the <b>Colour&nbsp;by</b> dropdown — the control at the top of the wheel below.
        </div>
      </div>
      {/* 2 — see it + interpret */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 340px) 1fr', gap: '1.3rem', alignItems: 'center', padding: '.9rem 1rem', borderBottom: '1px solid #e6eef3' }}>
        <div className="d-flex align-items-start" style={{ gap: 0 }}>
          <span style={stepNum}>2</span>
          <div style={{ width: 320 }}>
            <SunburstChart movie={movie} includeScenes metric={metric} size={320}
                           interactive={false} levelHighlight={EMPTY} onSelect={NOOP} autoSelectRoot={false} />
          </div>
        </div>
        <div>
          {/* generic "how to read it" — they clicked "how to see it on the wheel", so explain it here */}
          <div style={{ color: '#5b7a8c', fontSize: '.85rem', lineHeight: 1.5, marginBottom: '.55rem' }}>
            <b style={{ color: NAVY }}>How to read it:</b> start at 12 o’clock (Scene&nbsp;1), clockwise; rings go
            script → acts → sequences → scenes outward. Colour runs <b style={{ color: GREEN }}>green</b> (working)
            → <b style={{ color: RED }}>red</b> (needs work); <b style={{ color: '#94a3b8' }}>grey</b> means the unit
            doesn’t really owe this axis — neutral, not a problem, so read the colour only where it’s owed.
          </div>
          {/* the specific data reading for this insight */}
          <div style={{ color: SLATE, fontSize: '.9rem', lineHeight: 1.55 }}>
            {card.wheel_caption || templateWheelCaption(card, metricLabel, g, isStrength)}
          </div>
        </div>
      </div>
      {/* 3 — hand off to the live wheel */}
      <div style={{ padding: '.85rem 1rem', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={stepNum}>3</span>
        <span style={{ color: SLATE, fontSize: '.9rem', flex: 1, minWidth: 220 }}>
          Try it yourself — on the real wheel, click any scene or section for its grade and how to improve it.
        </span>
        <button type="button" onClick={() => onExplore && onExplore(metric)}
          style={{ fontWeight: 700, fontSize: '.86rem', color: '#fff', background: NAVY, border: 'none', borderRadius: 8, padding: '.4rem 1rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Take me ↓
        </button>
      </div>
    </div>
  );
}
WheelReveal.propTypes = { card: PropTypes.object, metric: PropTypes.string, metricLabel: PropTypes.string, movie: PropTypes.object, g: PropTypes.object, isStrength: PropTypes.bool, onExplore: PropTypes.func };

function InsightRow({ card, claim, g, movie, isStrength, open, onToggle, onExplore, first }) {
  const [wheelOpen, setWheelOpen] = useState(false);
  // Prefer the engine's chosen metric (once regenerated); fall back to deriving it from the claim.
  const metric = card.display_metric || metricForClaim(claim);
  const metricLabel = metric === 'overall' ? 'Triage' : (METRIC_LABELS[metric] || metric);
  const band = g.verdict_band;
  const cov = coverageText(card.footprint);
  const startText = card.writer_action || (card.role === 'primary_rewrite_pattern' ? g.first_move : '');
  const rightTag = isStrength ? 'Protect' : [BAND_LABEL[band], cov].filter(Boolean).join(' · ');
  const chevronColor = isStrength ? GREEN : MUTE;
  return (
    <div style={{ borderTop: first ? 'none' : '1px solid #e7edf1' }}>
      <button type="button" onClick={onToggle}
        className="w-100 d-flex align-items-center"
        style={{ gap: 10, border: 'none', background: open ? '#f8fbfd' : 'transparent', textAlign: 'left', cursor: 'pointer', padding: '.75rem 1rem' }}>
        <span style={{ color: chevronColor, flexShrink: 0, fontWeight: 700 }}>{isStrength ? '✓' : (open ? '▾' : '▸')}</span>
        <span style={{ fontWeight: 700, color: '#16242c', fontSize: '1.02rem', lineHeight: 1.25, flex: 1 }}>{card.title}</span>
        {rightTag && <span style={{ color: isStrength ? GREEN : MUTE, fontSize: '.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}>{rightTag}</span>}
      </button>
      {open && (
        <div style={{ padding: '0 1rem 1rem 2.2rem' }}>
          {card.human_read && <p style={{ margin: '0 0 .5rem', color: '#1f2d35', lineHeight: 1.55 }}>{card.human_read}</p>}
          {startText && (
            <div style={{ color: '#1f2d35', lineHeight: 1.5, fontSize: '.95rem' }}>
              <b style={{ color: isStrength ? GREEN : NAVY }}>{isStrength ? 'Keep: ' : 'Start here: '}</b>{startText}
            </div>
          )}
          <button type="button" onClick={() => setWheelOpen(o => !o)}
            style={{ marginTop: '.7rem', border: `1.5px solid ${isStrength ? '#9bd5aa' : '#b7cbd6'}`, background: '#fff', color: NAVY, fontWeight: 600, fontSize: '.86rem', borderRadius: 999, padding: '.32rem .95rem', cursor: 'pointer' }}>
            {wheelOpen ? '▾ Hide the wheel view' : '▸ How to see it on the wheel'}
          </button>
          {wheelOpen && <WheelReveal card={card} metric={metric} metricLabel={metricLabel} movie={movie} g={g} isStrength={isStrength} onExplore={onExplore} />}
        </div>
      )}
    </div>
  );
}
InsightRow.propTypes = { card: PropTypes.object, claim: PropTypes.object, g: PropTypes.object, movie: PropTypes.object, isStrength: PropTypes.bool, open: PropTypes.bool, onToggle: PropTypes.func, onExplore: PropTypes.func, first: PropTypes.bool };

export default function InsightList({ synthesis, movie, onExplore }) {
  const wv = synthesis && synthesis.writer_view;
  const g = wv && wv.tier_1_glance;
  const cards = (g && g.pattern_cards) || [];
  const claims = (synthesis && synthesis.machine_view && synthesis.machine_view.canonical_der_claims) || [];
  const rows = cards.map((card, i) => ({ card, claim: claims[i] || null, i, isStrength: card.role === 'protect_signal' }));
  const problems = rows.filter(r => !r.isStrength);
  const strength = rows.find(r => r.isStrength) || null;                 // one top strength only
  const ordered = [...problems, ...(strength ? [strength] : [])];
  const [openKey, setOpenKey] = useState(problems.length ? problems[0].i : (strength ? strength.i : null));
  if (!g || !ordered.length) return null;

  return (
    <div style={{ maxWidth: 1000, margin: '1.2rem auto 0', padding: '0 1.15rem' }}>
      <div style={{ color: NAVY, fontWeight: 700, fontSize: '1.05rem', margin: '0 0 .55rem' }}>
        Here’s what we found — most important first:
      </div>
      <div style={{ border: '1px solid #e7edf1', borderRadius: 12, background: '#fff', overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,.05)' }}>
        {ordered.map((r, idx) => (
          <InsightRow key={r.i} card={r.card} claim={r.claim} g={g} movie={movie} isStrength={r.isStrength} first={idx === 0}
            open={openKey === r.i} onToggle={() => setOpenKey(k => (k === r.i ? null : r.i))} onExplore={onExplore} />
        ))}
      </div>
    </div>
  );
}
InsightList.propTypes = { synthesis: PropTypes.object, movie: PropTypes.object, onExplore: PropTypes.func };
